import React, { useEffect, useState } from "react";
import { Carousel, CarouselContent } from "@shopflo/ui/carousel";
import type { CarouselApi } from "@shopflo/ui/carousel";
import { CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { IconButton } from "@shopflo/ui/icon-button";
import { Dialog, DialogContent } from "@shopflo/ui/dialog";
import { Icon } from "@shopflo/ui/icon";

interface FullPageCarouselProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  totalSlides: number;
  startIndex?: number;
}

const FullPageCarousel: React.FC<FullPageCarouselProps> = ({
  isOpen,
  onClose,
  children,
  totalSlides,
  startIndex = 0,
}) => {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(startIndex);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setSelectedIndex(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  // Scroll to startIndex when dialog opens and API is ready
  useEffect(() => {
    if (isOpen && api && startIndex >= 0 && startIndex < totalSlides) {
      api.scrollTo(startIndex);
    }
  }, [isOpen, api, startIndex, totalSlides]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="ui-max-w-none ui-w-screen ui-h-screen ui-p-0 ui-bg-transparent ui-border-none"
        style={{ animation: "none" }}
        withinContainer
        container={document.getElementById("checkout-ui-root")}
      >
        {/* Background overlay with blur effect */}
        <div className="ui-fixed ui-inset-0 ui-bg-black/70 ui-backdrop-blur-[10px]" />

        {/* Header with close button */}
        <div
          className="ui-fixed ui-top-0 ui-left-0 ui-right-0 ui-flex ui-justify-end ui-items-center ui-p-4 ui-z-50"
          onClick={onClose}
        >
          <IconButton
            size="lg"
            variant="ghost"
            className="ui-h-12 ui-w-12"
            IconComponent={X}
            iconProps={{ weight: "regular", color: "white" }}
          />
        </div>

        {/* Main image container */}
        <div
          className="ui-fixed ui-inset-0 ui-flex ui-flex-col ui-items-center ui-justify-center ui-pt-20 ui-pb-20"
          onClick={onClose}
        >
          <div className="ui-relative" onClick={(e) => e?.stopPropagation()}>
            <Carousel
              className="ui-w-full ui-h-full"
              setApi={setApi}
              opts={{
                watchDrag: totalSlides > 1,
              }}
            >
              <CarouselContent className="ui-h-full">
                {children}
              </CarouselContent>
            </Carousel>
          </div>
          {totalSlides > 1 && (
            <div
              className="ui-fixed ui-inset-x-0 ui-bottom-[15%] ui-flex ui-justify-center"
              onClick={(e) => e?.stopPropagation()}
            >
              <div className="ui-flex ui-justify-between ui-items-center ui-px-4 ui-max-w-[400px] ui-flex-1">
                <div
                  onClick={() => api?.scrollPrev()}
                  role="button"
                  aria-label="Previous slide"
                  className="ui-h-10 ui-w-10 ui-bg-black/20 ui-flex ui-justify-center ui-items-center ui-rounded-md"
                >
                  <Icon
                    size="sm"
                    IconComponent={CaretLeft}
                    iconProps={{ weight: "bold", color: "white" }}
                  />
                </div>
                <div className="ui-flex ui-items-center ui-gap-1 ui-p-1 ui-rounded-full">
                  {Array.from({ length: totalSlides }).map((_, i) => (
                    <button
                      key={i}
                      aria-label={`Go to slide ${i + 1}`}
                      onClick={() => api?.scrollTo?.(i)}
                      aria-current={selectedIndex === i}
                      className={`ui-h-1 ui-rounded-full ui-transition-all ui-duration-300 ui-ease-out ${
                        selectedIndex === i
                          ? "ui-w-[10px] ui-bg-white"
                          : "ui-w-1 ui-bg-white/50"
                      }`}
                    />
                  ))}
                </div>
                <div
                  className="ui-h-10 ui-w-10 ui-bg-black/20 ui-flex ui-justify-center ui-items-center ui-rounded-md"
                  onClick={() => api?.scrollNext()}
                  role="button"
                  aria-label="Next slide"
                >
                  <Icon
                    size="sm"
                    IconComponent={CaretRight}
                    iconProps={{ weight: "bold", color: "white" }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FullPageCarousel;
